import { Button, Input } from "@l30/ui";
import { useState } from "react";

import type { AiSourceRef } from "@l30/shared";

type DecisionTarget = {
  mapId: string;
  documentIds: string[];
};

export function decisionTargetFromSources(sources: AiSourceRef[]): DecisionTarget | null {
  const map = sources.find((source) => source.type === "ideation_map");
  if (!map) return null;

  return {
    mapId: map.id,
    documentIds: sources
      .filter((source) => source.type === "working_document")
      .map((source) => source.id),
  };
}

function officeConfirmLabel(args: Record<string, unknown>): string {
  const id = typeof args["documentId"] === "string" ? args["documentId"].slice(0, 8) : "…";
  if (Array.isArray(args["sheetEdits"]) && args["sheetEdits"].length > 0) {
    return `Overwrite same SharePoint Excel item and record a new version (${id}…)`;
  }
  if (args["mode"] === "replace_without_tc") {
    return `Replace entire SharePoint Word document WITHOUT Track Changes; formatting and layout may change (${id}…)`;
  }
  if (Array.isArray(args["replacements"]) && args["replacements"].length > 0) {
    return `Apply edits to same SharePoint Office file with Track Changes for Word (${id}…)`;
  }
  if (typeof args["bodyMd"] === "string" && args["bodyMd"].trim()) {
    return `Warning: replace the entire document WITHOUT Track Changes; formatting and layout may change (${id}…)`;
  }
  return `Overwrite same SharePoint Office file and record a new version (${id}…)`;
}

export function confirmToolLabel(toolId: string, args: Record<string, unknown>): string | null {
  if (toolId === "record_decision") {
    return "Record this decision on the project and linked docs?";
  }
  if (toolId === "revise_working_document") {
    return officeConfirmLabel(args);
  }
  if (toolId === "publish_portal_object" || toolId === "share_to_portal") {
    const title = typeof args["title"] === "string" ? `“${args["title"]}”` : "this object";
    const access = typeof args["accessLevel"] === "string" ? args["accessLevel"] : "read";
    return `Publish ${title} to the selected client portal with ${access} access?`;
  }
  if (toolId === "revoke_portal_object") {
    return "Revoke this publication from the selected client portal?";
  }
  return null;
}

export function RecordDecisionAction({
  mapId,
  documentIds,
  disabled,
  onConfirm,
}: {
  mapId: string;
  documentIds: string[];
  disabled: boolean;
  onConfirm: (args: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const trimmedSummary = summary.trim();

  if (!open) {
    return (
      <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Record decision
      </Button>
    );
  }

  return (
    <form
      className="flex flex-wrap items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!trimmedSummary) return;
        onConfirm({
          mapId,
          summary: trimmedSummary,
          ...(documentIds.length > 0 ? { documentIds } : {}),
        });
      }}
    >
      <Input
        aria-label="Decision summary"
        required
        value={summary}
        onChange={(event) => setSummary(event.target.value)}
        placeholder="Decision summary"
        className="min-w-48 flex-1"
      />
      <Button type="submit" size="sm" variant="secondary" disabled={disabled || !trimmedSummary}>
        Record on project
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </form>
  );
}
